import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Badge, Button, Spinner, Surface } from "@/shared/ui";
import { useImageJobStatus } from "../hooks/useImageJobStatus";
import { useVideoJobStatus } from "../hooks/useVideoJobStatus";
import type { ToolState } from "../types";
import { ImageResultCard } from "./ImageResultCard";
import { VideoResultCard } from "./VideoResultCard";
import { CheckIcon, ImageIcon, FilmIcon, ToolIcon } from "./icons";

type ToolKind = "image" | "video" | "generic";

function toolKind(name: string | undefined): ToolKind {
  if (!name) return "generic";
  if (name.includes("image")) return "image";
  if (name.includes("video")) return "video";
  return "generic";
}

/**
 * The `generate_image` / `generate_video` tools resolve immediately with a
 * queued job (`result.output.job_id`); the real asset is produced async. Pull
 * that id so the card can poll the job instead of claiming "Generated" early.
 */
function toolJobId(tool: ToolState): string | null {
  const output = tool.result?.output;
  if (!output || typeof output !== "object") return null;
  const id = (output as Record<string, unknown>).job_id;
  return typeof id === "string" && id ? id : null;
}

/**
 * The resolved aspect ratio the tool picked for this job ("16:9" | "1:1" |
 * "9:16" | …). Lets the loader box render at the final asset's shape before any
 * bytes arrive, so there's no reshape when the image/video lands.
 */
function toolAspectRatio(tool: ToolState): string | null {
  const output = tool.result?.output;
  if (!output || typeof output !== "object") return null;
  const ratio = (output as Record<string, unknown>).aspect_ratio;
  return typeof ratio === "string" && ratio ? ratio : null;
}

function KindIcon({ kind }: { kind: ToolKind }) {
  if (kind === "image") return <ImageIcon className="size-[18px]" />;
  if (kind === "video") return <FilmIcon className="size-[18px]" />;
  return <ToolIcon className="size-[18px]" />;
}

/**
 * The interactive tool gate. On `tool_proposal` the stream blocks here for the
 * user's call; approve → the run executes the tool and resumes streaming. Walks
 * the full lifecycle: proposed → executing → succeeded / failed / declined /
 * expired / rejected.
 */
export function ToolConfirmCard({
  tool,
  onApprove,
  onCancel,
}: {
  tool: ToolState;
  onApprove: () => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const [acted, setActed] = useState(false);

  const kind = toolKind(tool.proposal?.tool_name);
  const model = tool.proposal?.model ?? "";
  const cost = tool.proposal?.credit_cost ?? 0;

  // The image/video tools report success the instant their job is queued, but
  // the asset is still being produced async — poll the job so the card shows a
  // real "Generating…" state until it actually finishes.
  const imageJobId = kind === "image" && tool.status === "succeeded" ? toolJobId(tool) : null;
  const videoJobId = kind === "video" && tool.status === "succeeded" ? toolJobId(tool) : null;
  const { phase: imagePhase, output: imageOutput } = useImageJobStatus(imageJobId);
  const { phase: videoPhase, output: videoOutput } = useVideoJobStatus(videoJobId);
  const requestedRatio = toolAspectRatio(tool);

  // Image/video generation drops all card chrome: once the tool has a queued
  // job we render ONLY the result box (loader → asset), no header/icon/border.
  // Every other tool keeps the full card below.
  if (imageJobId !== null) {
    return (
      <div className="my-3">
        <ImageResultCard phase={imagePhase} output={imageOutput} requestedRatio={requestedRatio} />
      </div>
    );
  }
  if (videoJobId !== null) {
    return (
      <div className="my-3">
        <VideoResultCard phase={videoPhase} output={videoOutput} requestedRatio={requestedRatio} />
      </div>
    );
  }

  const headline =
    kind === "image"
      ? t("chat.toolConfirm.headlineImage", { model })
      : kind === "video"
        ? t("chat.toolConfirm.headlineVideo", { model })
        : t("chat.toolConfirm.headlineGeneric", { model });

  return (
    <Surface
      tone="panel-2"
      border="none"
      radius="md"
      shadow="soft"
      className="my-3 flex flex-col gap-3 p-4"
    >
      <div className="flex items-center gap-3">
        <span className="grid size-9 shrink-0 place-items-center rounded-md bg-bubble text-ink">
          <KindIcon kind={kind} />
        </span>
        <span className="min-w-0 flex-1 font-serif text-[16px] font-medium text-ink">{headline}</span>
        {tool.status === "proposed" ? (
          <Badge tone="soft" size="sm" aria-label={t("chat.toolConfirm.costAria", { cost })}>
            {t("chat.toolConfirm.costChip", { cost })}
          </Badge>
        ) : null}
      </div>

      {tool.status === "proposed" ? (
        <div className="flex gap-2">
          <Button
            size="sm"
            variant="ink"
            disabled={acted}
            onClick={() => {
              setActed(true);
              onApprove();
            }}
          >
            {t("chat.toolConfirm.generate")}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            disabled={acted}
            onClick={() => {
              setActed(true);
              onCancel();
            }}
          >
            {t("chat.toolConfirm.cancel")}
          </Button>
        </div>
      ) : null}

      {tool.status === "executing" ? (
        <div className="flex items-center gap-2.5 text-ink-muted">
          <Spinner size={15} />
          <span className="font-sans text-[13px]">
            {kind === "image"
              ? t("chat.toolConfirm.executingImage", { model })
              : kind === "video"
                ? t("chat.toolConfirm.executingVideo", { model })
                : t("chat.toolConfirm.executingGeneric")}
          </span>
        </div>
      ) : null}

      {tool.status === "succeeded" ? (
        <div className="flex items-center gap-2 text-ink">
          <CheckIcon className="size-4" />
          <span className="font-sans text-[13px] font-medium">
            {t("chat.toolConfirm.generated")}
          </span>
          {tool.result?.url ? (
            <a
              href={tool.result.url}
              target="_blank"
              rel="noreferrer noopener"
              className="font-sans text-[13px] font-medium text-ink underline underline-offset-2 hover:text-ink-strong"
            >
              {t("messages.open")}
            </a>
          ) : null}
        </div>
      ) : null}

      {tool.status === "failed" || tool.status === "rejected" ? (
        <p className="font-sans text-[13px] text-ink-muted">
          {tool.error || t("chat.toolConfirm.failed")}
        </p>
      ) : null}

      {tool.status === "declined" ? (
        <p className="font-sans text-[13px] text-ink-muted">{t("chat.toolConfirm.cancelled")}</p>
      ) : null}

      {tool.status === "expired" ? (
        <p className="font-sans text-[13px] text-ink-muted">{t("chat.toolConfirm.expired")}</p>
      ) : null}
    </Surface>
  );
}
